import api, { Derogatory, DerogatoryType, PermissionType } from '@api';
import { Button, Link as MuiLink, Typography } from '@mui/material';
import { useAsyncEffect, useParams } from '@tsp-ui/core';
import {
    ProgressIndicator, RoutedDialogManager, usePageMessage
} from '@tsp-ui/core/components';
import { withAuth } from '@utils';
import { LoanDetailPageParams } from '@views/loans/LoanDetailPage';
import {
    Dispatch, SetStateAction, createContext, useCallback, useState
} from 'react';
import { Link } from 'react-router-dom';

import DerogatoryCard from './DerogatoryCard';
import { DerogatoryDialog } from './DerogatoryDialog';
import styles from './DerogatorySection.module.scss';


export const DerogatoryContext = createContext<{
    derogatories: Derogatory[];
    setDerogatories: Dispatch<SetStateAction<Derogatory[]>>;
}>({
    derogatories: [],
    setDerogatories: () => {}
});

export function DerogatorySection() {
    const [ derogatories, setDerogatories ] = useState<Derogatory[]>([]);
    const [ loading, setLoading ] = useState(true);
    const { accountID: clientId, loanID: loanId } = useParams<LoanDetailPageParams>();
    const pageMessage = usePageMessage();

    useAsyncEffect(useCallback(async () => {
        if (!clientId || !loanId) {
            return;
        }

        setLoading(true);

        try {
            const records = await api.derogatory.getDerogatories(clientId, loanId);
            setDerogatories(records);
        } catch (error) {
            pageMessage.handleApiError('An error occurred while fetching derogatory records', error);
        }

        setLoading(false);
    }, [
        clientId, loanId, pageMessage
    ]));

    const bankruptcyForeclosureSection = derogatories.filter(derogatory => [
        DerogatoryType.BANKRUPTCY,
        DerogatoryType.FORECLOSURE,
        DerogatoryType.SHORT_SALE
    ].includes(derogatory.type));

    const judgementLienSection = derogatories.filter(derogatory => [
        DerogatoryType.OUTSTANDING_JUDGEMENT,
        DerogatoryType.LIEN
    ].includes(derogatory.type));

    const collectionsChargeOffsSection = derogatories.filter(derogatory => [
        DerogatoryType.OPEN_COLLECTIONS,
        DerogatoryType.CHARGE_OFFS
    ].includes(derogatory.type));

    return loading ? <ProgressIndicator /> : (
        <DerogatoryContext.Provider value={{
            derogatories,
            setDerogatories
        }}
        >
            <Button
                component={Link}
                to="derogatory"
            >
                Add Derogatory
            </Button>

            <div className={styles.root}>
                {!!bankruptcyForeclosureSection.length && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        className={styles.sectionLabel}
                    >
                        Bankruptcies, Foreclosures & Short Sales
                    </Typography>
                )}

                {bankruptcyForeclosureSection.map(derogatory => (
                    <DerogatoryCard
                        key={derogatory.id}
                        derogatory={derogatory}
                    />
                ))}

                {!!judgementLienSection.length && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        className={styles.sectionLabel}
                    >
                        Outstanding Judgements & Liens
                    </Typography>
                )}

                {judgementLienSection.map(derogatory => (
                    <DerogatoryCard
                        key={derogatory.id}
                        derogatory={derogatory}
                    />
                ))}

                {!!collectionsChargeOffsSection.length && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        className={styles.sectionLabel}
                    >
                        Open Collections & Charge Offs
                    </Typography>
                )}

                {collectionsChargeOffsSection.map(derogatory => (
                    <DerogatoryCard
                        key={derogatory.id}
                        derogatory={derogatory}
                    />
                ))}

                {!derogatories.length && (
                    <Typography
                        variant="body2"
                        color="textSecondary"
                    >
                        No derogatory records found.&nbsp;
                        <MuiLink
                            component={Link}
                            to="derogatory"
                        >
                            Click here
                        </MuiLink> to add a new one.
                    </Typography>
                )}
            </div>

            <RoutedDialogManager routes={dialogRoutes} />
        </DerogatoryContext.Provider>
    );
}

const dialogRoutes = {
    derogatory: withAuth(DerogatoryDialog, [ PermissionType.MANAGE_DEROGATORIES ]),
    'derogatory/:derogatoryId': withAuth(DerogatoryDialog, [ PermissionType.MANAGE_DEROGATORIES ])
};
